'use client'

import { BuildingIcon } from 'lucide-react'

import { Button } from '@ui/web/components'
import { HouseIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import { buildUrl } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

/** The app the user opened — web is for families and children, school is for teachers. */
export type WrongPlatform = 'web' | 'school'

export interface WrongPlatformStateProps {
  platform: WrongPlatform
  className?: string
}

const CONTENT = {
  web: {
    icon: HouseIcon,
    title: 'Ruang ini untuk keluarga dan anak',
    description: 'Akunmu belum terdaftar sebagai orang tua murid',
    createLabel: 'Buat akun keluarga',
    createHref: () => buildUrl('account', '/buat-akun/keluarga').href,
    backLabel: 'Lanjut ke ruang sekolah',
    backHref: () => buildUrl('school', '/').href,
  },
  school: {
    icon: BuildingIcon,
    title: 'Ini ruang PiBo Sekolah',
    description: 'Akunmu belum terdaftar sebagai guru di sekolah',
    createLabel: 'Buat akun sekolah',
    createHref: () => buildUrl('account', '/buat-akun/sekolah').href,
    backLabel: 'Lanjut ke ruang keluarga',
    backHref: () => buildUrl('web', '/').href,
  },
} as const

/**
 * Shown by an app's auth guard when a signed-in adult only has the other kind of organization —
 * a teacher on web, a parent on school. Nothing is created until they choose to.
 */
export function WrongPlatformState({ platform, className }: WrongPlatformStateProps) {
  const content = CONTENT[platform]
  const Icon = content.icon

  return (
    <div
      className={cn('srd:flex srd:min-h-dvh srd:w-full srd:flex-col srd:items-center srd:justify-center', className)}
    >
      <div className='srd:gap-6 srd:px-4 srd:py-12 srd:rounded-xl srd:flex srd:flex-col srd:items-center srd:justify-center srd:bg-neutral-white'>
        <Icon className='srd:size-14 srd:text-blue-100' />

        <div className='srd:flex srd:max-w-md srd:flex-col srd:items-center srd:gap-2 srd:text-center'>
          <h1 className={cn(typographyVariants({ variant: 'headline-md' }), 'srd:text-neutral-black')}>
            {content.title}
          </h1>
          <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-grey-70')}>{content.description}</p>
        </div>

        <div className='srd:flex srd:flex-col srd:gap-3'>
          <Button asChild>
            <a href={content.backHref()}>{content.backLabel}</a>
          </Button>

          <Button asChild variant='ghost'>
            <a href={content.createHref()}>{content.createLabel}</a>
          </Button>
        </div>
      </div>
    </div>
  )
}
